import React, { useState, useEffect } from "react";

import {
    Box,
    Paper,
    Divider,
    TextField,
    Grid,
    Typography,
    Button,
    IconButton,
    InputAdornment,
    styled,
    Card,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableFooter,
    Chip
} from '@mui/material';

import { useNavigate, useParams } from "react-router-dom";
import { useUsers, userTypes, targetTypes } from "../../../hooks/api/useUsers";
import { useBranches, branchTypes, targetTypes as branchTargetTypes } from "../../../hooks/api/useBranches";
import { useBranchUsers, branchUserTypes, targetTypes as branchUserTargetTypes } from '../../../hooks/api/useBranchUsers';

import { useSystemPrompt } from "../../../hooks/useSystemPrompt";
import { useAppContextFeatures } from "../../../context/AppContext";
import { useSystemRoutes, routeTypes } from "../../../hooks/useSystemRoutes";
import { useSystemHead, headTitles } from '../../../hooks/useSystemHead';
import { usePermissions, scopeIds, scopeTypes, functionTypes } from "../../../hooks/usePermissions";
import { useTranslations } from '../../../hooks/useTranslations';

import CommonSectionHeader from "../../common/CommonSectionHeader";
import CommonBreadcumbs from "../../common/CommonBreadcumbs";
import CommonRestricted from "../../common/CommonRestricted";

const StyledCard = styled(Card)(({ theme }) => ({
    padding: theme.spacing(3)
}));

const StyledLabel = styled(Typography)(({ theme }) => ({
    fontSize: '0.9rem',
    fontWeight: 800
}))

const StyledContent = styled(Typography)(({ theme }) => ({
    fontSize: '0.9rem'
}))

const StyledDivider = styled(Divider)(({ theme }) => ({
    mt: 1,
    mb: 0,
    borderStyle: 'dashed'
}))

export default function ViewRetailer({
    routeType
}) {
    const { t } = useTranslations({ ns: ["common", "@user"] })

    const { id } = useParams();

    const { setSystemLoading } = useAppContextFeatures();

    const { promptMessage } = useSystemPrompt();
    const { get: getUser } = useUsers({ userType: userTypes.retail, targetType: targetTypes.retail });
    const { list: listBranches, filterOperatorTypes } = useBranches({ branchType: branchTypes.retail, targetType: branchTargetTypes.retail });
    const { list: listBranchUsers } = useBranchUsers({ branchUserType: branchUserTypes.retailer, targetType: branchUserTargetTypes.retailer });

    const { getRoute } = useSystemRoutes(routeType)
    const { getHtmlHead } = useSystemHead(routeType)
    const { isPermissionAllow } = usePermissions(routeType, scopeIds.user.id, scopeTypes.user.retail.id)

    const navigate = useNavigate();
    const [data, setData] = useState()
    const [dataBranches, setDataBranches] = useState([])
    const [dataBranchUsers, setDataBranchUsers] = useState([])

    const fetchData = async () => {

        setSystemLoading(true);

        try {

            let response = await getUser({ id });

            setData(response?.data)

        } catch (error) {
            promptMessage(false, `${error.message}`)
        }

        setSystemLoading(false)

    }

    const fetchDataBranch = async () => {

        setSystemLoading(true);

        try {

            const props = {
                limit: -1,
                skip: 0,
                sortInfo: {
                    id: "id",
                    dir: 0,
                },
                filterValue: [
                    {
                        name: "user_id",
                        operator: filterOperatorTypes.eq,
                        value: id
                    }
                ]
            }

            const { data } = await listBranches(props)

            setDataBranches(data?.branches)

        } catch (error) {
            promptMessage(false, `${error?.message}`)
        }

        setSystemLoading(false);

    }

    const fetchDataBranchUser = async () => {

        setSystemLoading(true);

        try {

            const props = {
                limit: -1,
                skip: 0,
                sortInfo: {
                    id: "id",
                    dir: 0,
                },
                filterValue: [
                    {
                        name: "parent_user_id",
                        operator: filterOperatorTypes.eq,
                        value: id
                    }
                ]
            }

            const { data } = await listBranchUsers(props)

            setDataBranchUsers(data?.branch_users)

        } catch (error) {
            promptMessage(false, `${error?.message}`)
        }

        setSystemLoading(false);

    }

    useEffect(() => {

        fetchData();
        fetchDataBranch()
        fetchDataBranchUser()

    }, [])

    const getBreadcumbActionMenus = () => {

        let props = []

        if (isPermissionAllow({ functionType: functionTypes.pageUpdate.id })) {
            props.push({
                displayText: t("editProfile"),
                href: getRoute(`users/retailer/update/${id}`)
            })
        }

        return props

    }

    const RenderFormProfile = () => {

        return (
            <StyledCard>
                <Grid container spacing={0.5}>
                    <Grid item xs={6} sm={6} md={6} textAlign={"left"}>
                        <CommonSectionHeader
                            title={t(`profile`)}
                            subTitle={t(`retailProfileDetails`)}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <StyledDivider />
                    </Grid>

                    <Grid item xs={12} sm={12} md={12} sx={{ mt: 2 }}>
                        <Grid container spacing={0.5}>

                            <Grid item xs={12} sm={12} md={4} textAlign={"left"}>
                                <StyledLabel>{t('username')}</StyledLabel>
                            </Grid>

                            <Grid item xs={12} sm={12} md={8} textAlign={"left"}>
                                <StyledContent>
                                    {data?.retailer?.username}
                                </StyledContent>
                            </Grid>

                            <Grid item xs={12} sm={12} md={4} textAlign={"left"}>
                                <StyledLabel>{t('name')}</StyledLabel>
                            </Grid>

                            <Grid item xs={12} sm={12} md={8} textAlign={"left"}>
                                <StyledContent>
                                    {`${data?.retailer?.firstname} ${data?.retailer?.lastname? data?.retailer.lastname : ""}`}
                                </StyledContent>
                            </Grid>

                            <Grid item xs={12} sm={12} md={4} textAlign={"left"}>
                                <StyledLabel>{t('contact')}</StyledLabel>
                            </Grid>
                            <Grid item xs={12} sm={12} md={8} textAlign={"left"}>
                                <StyledContent>
                                    {data?.retailer?.contact}
                                </StyledContent>
                            </Grid>

                            <Grid item xs={12} sm={12} md={4} textAlign={"left"}>
                                <StyledLabel>{t('email')}</StyledLabel>
                            </Grid>
                            <Grid item xs={12} sm={12} md={8} textAlign={"left"}>
                                <StyledContent>
                                    {data?.retailer?.email}
                                </StyledContent>
                            </Grid>

                        </Grid>
                    </Grid>

                </Grid>
            </StyledCard>
        )
    }

    const RenderFormCompany = () => {
        return (
            <StyledCard>
                <Grid container spacing={0.5}>
                    <Grid item xs={12} sm={12} md={12} textAlign={"left"}>

                        <CommonSectionHeader
                            title={t(`company`)}
                            subTitle={t(`detailsOfCompanyInformation`)}
                        />

                    </Grid>
                    <Grid item xs={12}>
                        <StyledDivider />
                    </Grid>
                    <Grid item xs={12} sm={12} md={12} sx={{ mt: 2 }}>
                        <Grid container spacing={0.5}>

                            <Grid item xs={12} sm={12} md={4} textAlign={"left"}>
                                <StyledLabel>{t('companyName')}</StyledLabel>
                            </Grid>
                            <Grid item xs={12} sm={12} md={8} textAlign={"left"}>
                                <StyledContent>
                                    {data?.retailer.company_name}
                                </StyledContent>
                            </Grid>

                            <Grid item xs={12} sm={12} md={4} textAlign={"left"}>
                                <StyledLabel>{t('regNo')}</StyledLabel>
                            </Grid>
                            <Grid item xs={12} sm={12} md={8} textAlign={"left"}>
                                <StyledContent>
                                    {data?.retailer.reg_no}
                                </StyledContent>
                            </Grid>

                            <Grid item xs={12} sm={12} md={4} textAlign={"left"}>
                                <StyledLabel>
                                    {t('code')}
                                </StyledLabel>
                            </Grid>
                            <Grid item xs={12} sm={12} md={8} textAlign={"left"}>
                                <StyledContent>
                                    {data?.retailer.code}
                                </StyledContent>
                            </Grid>

                            <Grid item xs={12} sm={12} md={4} textAlign={"left"}>
                                <StyledLabel>
                                    {t('accountCode')}
                                </StyledLabel>
                            </Grid>
                            <Grid item xs={12} sm={12} md={8} textAlign={"left"}>
                                <StyledContent>
                                    {data?.retailer.account_code}
                                </StyledContent>
                            </Grid>

                        </Grid>
                    </Grid>
                </Grid>
            </StyledCard>
        )
    }

    const RenderFormOutlet = () => {
        
        return (
            <Grid container spacing={0.5}> 
                <Grid item xs={6} sm={6} md={12} display={"flex"} justifyContent={"flex-start"} alignItems={"left"} sx={{ml: 1}}>
                    <CommonSectionHeader
                        title={t(`outlets`)}
                        subTitle={t(`outletDetailsInformation`)}
                    />
                </Grid>

                {
                    (!dataBranches.length)?
                        <Grid item xs={12} sm={12} md={12} textAlign={"center"}>
                            <StyledCard
                                sx={{
                                    mt : 1,
                                    p : 0
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontWeight: 500,
                                        fontSize: 20,
                                        height: 100,
                                        textAlign: 'center',
                                        alignContent: 'center'
                                    }}
                                >
                                    {t('thereIsNoOutletAddedAtTheMoment')}
                                </Typography>
                            </StyledCard>
                        </Grid>
                        :
                        <>
                            <Grid item xs={12} sm={12} md={12} textAlign={"right"}>
                                <StyledCard
                                    sx={{
                                        mt : 1,
                                        p : 0
                                    }}
                                >
                                    <Grid container rowSpacing={0}>
                                        <Grid item xs={12} sm={12} md={12}>
                                            <TableContainer>
                                                <Table
                                                    size="small"
                                                    stickyHeader
                                                >
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell width={20}>#</TableCell>
                                                            <TableCell width={120}>{t('outletName')}</TableCell>
                                                            <TableCell width={100}>{t('outletCode')}</TableCell>
                                                            <TableCell width={100}>{t('outletContact')}</TableCell>
                                                            <TableCell width={100}>{t('outletEmail')}</TableCell>
                                                            <TableCell width={130}>{t('Area')}</TableCell>
                                                            <TableCell width={100}>{t('HQ')}</TableCell>
                                                        </TableRow>
                                                    </TableHead>

                                                    <TableBody>
                                                        {
                                                            dataBranches?.map((item, index) => {
                                                                return(
                                                                    <TableRow key={index}>
                                                                        <TableCell>{(index + 1)}</TableCell>
                                                                        <TableCell>{item?.branch_name}</TableCell>
                                                                        <TableCell>{item?.branch_code}</TableCell>
                                                                        <TableCell>{item?.branch_contact}</TableCell>
                                                                        <TableCell>{item?.branch_email}</TableCell>
                                                                        <TableCell>{`${item?.area_name}, ${item?.state_name}`}</TableCell>
                                                                        <TableCell>{item?.is_hq === '1'? t('mainOutlet') : t('normalOutlet')}</TableCell>
                                                                    </TableRow>
                                                                )
                                                            })
                                                        }
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </Grid>
                                    </Grid>
                                </StyledCard>

                            </Grid>
                        </>
                }
            </Grid>
        )
    }

    const RenderFormOutletUser = () => {
        return (
            <Grid container spacing={0.5}> 
                <Grid item xs={6} sm={6} md={12} display={"flex"} justifyContent={"flex-start"} alignItems={"left"} sx={{ml: 1}}>
                    <CommonSectionHeader
                        title={t(`outletUsers`)}
                        subTitle={t(`outletUserDetailsInformation`)}
                    />
                </Grid>

                {
                    (!dataBranchUsers.length)?
                        <Grid item xs={12} sm={12} md={12} textAlign={"center"}>
                            <StyledCard
                                sx={{
                                    mt : 1,
                                    p : 0
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontWeight: 500,
                                        fontSize: 20,
                                        height: 100,
                                        textAlign: 'center',
                                        alignContent: 'center'
                                    }}
                                >
                                    {t('thereIsNoOutletUserBeenAssignedAtTheMoment')}
                                </Typography>
                            </StyledCard>
                        </Grid>
                        :
                        <>
                            <Grid item xs={12} sm={12} md={12} textAlign={"right"}>
                                <StyledCard
                                    sx={{
                                        mt : 1,
                                        p : 0
                                    }}
                                >
                                    <Grid container rowSpacing={0}>
                                        <Grid item xs={12} sm={12} md={12}>
                                            <TableContainer>
                                                <Table
                                                    size="small"
                                                    stickyHeader
                                                >
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell width={20}>#</TableCell>
                                                            <TableCell width={150}>{t('username')}</TableCell>
                                                            <TableCell width={150}>{t('name')}</TableCell>
                                                            <TableCell width={150}>{t('email')}</TableCell>
                                                            <TableCell width={150}>{t('contact')}</TableCell>
                                                        </TableRow>
                                                    </TableHead>

                                                    <TableBody>
                                                        {
                                                            dataBranchUsers?.map((item, index) => {
                                                                return(
                                                                    <TableRow key={index}>
                                                                        <TableCell>{(index + 1)}</TableCell>
                                                                        <TableCell>{item?.username}</TableCell>
                                                                        <TableCell>{`${item?.firstname} ${item?.lastname}`}</TableCell>
                                                                        <TableCell>{item?.email}</TableCell>
                                                                        <TableCell>{item?.contact}</TableCell>
                                                                    </TableRow>
                                                                )
                                                            })
                                                        }
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </Grid>
                                    </Grid>
                                </StyledCard>

                            </Grid>
                        </>
                }
            </Grid>
        )
    }

    const RenderForm = () => {
        return (
            <>
                <Grid container spacing={1}>
                    <Grid item xs={12} sm={12} md={7} textAlign={"left"}>
                        <RenderFormCompany />
                    </Grid>
                    <Grid item xs={12} sm={12} md={5} textAlign={"left"}>
                        <RenderFormProfile />
                    </Grid>

                    <Grid item xs={12} sm={12} md={12} textAlign={"left"} sx={{mt: 1}}>
                        <RenderFormOutlet />
                    </Grid>

                    <Grid item xs={12} sm={12} md={12} textAlign={"left"} sx={{mt: 1}}>
                        <RenderFormOutletUser />
                    </Grid>
                </Grid>

                <Grid container spacing={1} sx={{ pl: 2, pr: 2 }}>
                    <Grid item md={12} textAlign={"center"}>
                        <Button
                            type="button"
                            variant="contained"
                            sx={{
                                mt: 3,
                                mb: 2
                            }}
                            onClick={() => navigate(-1)}
                        >
                            {t('back')}
                        </Button>
                    </Grid>
                </Grid>
            </>
        )
    }

    return (
        <Box>
            {
                getHtmlHead({ routeType, title: headTitles.viewRetailer })
            }

            <CommonBreadcumbs
                routeType={routeType}
                pageTitle={t('viewRetailer')}
                actionMenus={
                    getBreadcumbActionMenus()
                }
                breadcrumbs={[
                    {
                        displayText: t("retailers"),
                        props: {
                            // underline: "hover",
                            href: getRoute("users/retailers")
                        }
                    },
                    {
                        displayText: t("viewRetailer"),
                        props: {
                            color: 'secondary.main',
                        }
                    },
                ]}
            />

            {
                isPermissionAllow({ functionType: functionTypes.pageView.id }) ?
                    (
                        <RenderForm />
                    )
                    :
                    (
                        <CommonRestricted />
                    )
            }

        </Box>
    )
}